import React, { useState, useEffect, useCallback } from 'react';
import { getPendingSellers, verifySeller } from '../../services/adminService';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';

const ProfessionalSellers = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState({}); // To track individual seller updates

  const fetchSellers = useCallback(async () => {
    try {
      setLoading(true);
      const pendingSellers = await getPendingSellers();
      setSellers(pendingSellers);
      setError('');
    } catch (err) {
      setError(err.message);
      setSellers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  const handleVerification = async (uid, status) => {
    setUpdating(prev => ({ ...prev, [uid]: true }));
    setError('');
    try {
      await verifySeller(uid, status);
      // On success, remove the seller from the list in the UI
      setSellers(prevSellers => prevSellers.filter(seller => String(seller.id) !== String(uid)));
    } catch (err) {
      setError(`Failed to update seller ${uid}: ${err.message}`);
    } finally {
      setUpdating(prev => ({ ...prev, [uid]: false }));
    }
  };

  return (
    <>
      <div className="admin-page-header">
        <h2>Verify Professional Sellers</h2>
        <p>Review and approve or reject new professional seller registrations.</p>
      </div>

      <div className="admin-card">
        <h2>Pending Verifications</h2>
        {loading && <div className="loading-spinner"><FaSpinner /> Loading sellers...</div>}
        {error && <div className="admin-error-message">Error: {error}</div>}
        {!loading && (
          <div className="admin-table-container">
            {sellers.length > 0 ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Display Name</th>
                    <th>Business Name</th>
                    <th>Business Address</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sellers.map(seller => (
                    <tr key={seller.id}>
                      <td>{seller.email}</td>
                      <td>{seller.displayName || 'N/A'}</td>
                      <td>{seller.businessName || 'N/A'}</td>
                      <td>{seller.businessAddress || 'N/A'}</td>
                      <td className="actions-cell">
                        <button 
                          className="admin-btn admin-btn-success" 
                          onClick={() => handleVerification(seller.id, 'approved')}
                          disabled={updating[seller.id]}
                        >
                          {updating[seller.id] ? <FaSpinner className="spinning"/> : <FaCheckCircle />} Approve
                        </button>
                        <button 
                          className="admin-btn admin-btn-danger" 
                          onClick={() => handleVerification(seller.id, 'rejected')}
                          disabled={updating[seller.id]}
                        >
                          {updating[seller.id] ? <FaSpinner className="spinning"/> : <FaTimesCircle />} Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="admin-empty-state">
                <p>No professional sellers are currently pending verification.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ProfessionalSellers;
